<template>
  <div class="miniuser-detail">
    <el-card shadow="hover" class="action-card">
      <div class="action-bar">
        <div class="left-actions">
          <el-button plain @click="goBack">返回列表</el-button>
        </div>
        <div class="meta">
          <span>最近登录：{{ formatDate(userDetail.last_login_at) }}</span>
          <span v-if="userDetail.last_login_ip">IP：{{ userDetail.last_login_ip }}</span>
        </div>
        <div class="right-actions">
          <el-button type="primary" plain @click="openSuperDialog">设置管理员</el-button>
          <el-button type="warning" plain @click="openActivateVip">开通VIP</el-button>
        </div>
      </div>
    </el-card>

    <el-row :gutter="20" class="content-row">
      <el-col :xs="24" :md="16">
        <!-- 基本信息 -->
        <el-card shadow="hover" class="info-card">
          <div slot="header" class="card-header">
            <span>基本信息</span>
            <div class="header-actions">
              <template v-if="!editingBasic">
                <el-button type="text" icon="el-icon-edit" @click="startBasicEdit">编辑</el-button>
              </template>
              <template v-else>
                <el-button type="text" icon="el-icon-check" @click="saveBasicEdit">保存</el-button>
                <el-button type="text" icon="el-icon-close" @click="cancelBasicEdit">取消</el-button>
              </template>
            </div>
          </div>
          <div class="info-grid" v-if="!editingBasic">
            <div class="info-item">
              <p class="label">昵称</p>
              <p class="value">{{ userDetail.nickname || '--' }}</p>
            </div>
            <div class="info-item">
              <p class="label">手机号</p>
              <p class="value">{{ userDetail.phone || '--' }}</p>
            </div>
            <div class="info-item">
              <p class="label">性别称呼</p>
              <p class="value">{{ userDetail.gendervalue || '--' }}</p>
            </div>
            <div class="info-item">
              <p class="label">VIP状态</p>
              <p class="value">
                <el-tag :type="userDetail.is_vip ? 'success' : 'info'">
                  {{ userDetail.is_vip ? 'VIP 用户' : '普通用户' }}
                </el-tag>
              </p>
            </div>
          </div>
          <div class="info-grid" v-else>
            <div class="info-item">
              <p class="label">昵称</p>
              <el-input v-model="basicForm.nickname" size="small" />
            </div>
            <div class="info-item">
              <p class="label">手机号</p>
              <el-input v-model="basicForm.phone" size="small" />
            </div>
            <div class="info-item">
              <p class="label">性别称呼</p>
              <el-input v-model="basicForm.gendervalue" size="small" />
            </div>
            <div class="info-item">
              <p class="label">VIP状态</p>
              <el-switch
                v-model="basicForm.is_vip"
                :active-value="1"
                :inactive-value="0"
                active-text="VIP"
                inactive-text="普通"
              />
            </div>
          </div>
        </el-card>

        <!-- 账号标识 -->
        <el-card shadow="hover" class="info-card">
          <div slot="header" class="card-header">
            <span>账号信息</span>
          </div>
          <div class="info-grid">
            <div class="info-item">
              <p class="label">User ID</p>
              <p class="value">{{ userDetail.user_id || '--' }}</p>
            </div>
            <div class="info-item">
              <p class="label">User Agent</p>
              <p class="value">{{ parseUserAgent(userDetail.last_login_user_agent) }}</p>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :md="8">
        <el-card shadow="hover" class="info-card">
          <div slot="header" class="card-header">
            <span>会员记录</span>
          </div>
          <el-timeline v-if="memberships.length" class="membership-timeline">
            <el-timeline-item
              v-for="item in memberships"
              :key="item.id"
              :timestamp="formatRange(item.start_date, item.end_date)"
              :color="item.is_active ? '#67c23a' : '#dcdfe6'"
              placement="top"
            >
              <div class="timeline-item">
                <div class="timeline-header">
                  <div class="title">
                    {{ item.membership_no }}
                  </div>
                  <el-tag 
                    size="small" 
                    :type="item.is_active ? 'success' : 'info'"
                    effect="plain"
                  >
                    {{ item.is_active ? '生效中' : '已失效' }}
                  </el-tag>
                </div>
                <div class="timeline-content" @click="goMembershipDetail(item)">
                  <div class="info-row">
                    <span class="info-label">金额：</span>
                    <span class="info-value amount">¥ {{ Number(item.amount || 0).toFixed(2) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">类型：</span>
                    <span class="info-value">{{ item.vip_type.name_zh }}</span>
                  </div>
                  <div class="info-row" v-if="item.notes">
                    <span class="info-label">备注：</span>
                    <span class="info-value">{{ item.notes }}</span>
                  </div>
                </div>
              </div>
            </el-timeline-item>
          </el-timeline>
          <div v-else class="empty-tip">
            <i class="el-icon-info"></i> 暂无会员记录
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Super Admin Dialog -->
    <el-dialog
      title="设置管理员权限"
      :visible.sync="superDialogVisible"
      width="30%"
      :close-on-click-modal="false"
    >
      <el-form label-width="100px" size="small" class="edit-form">
        <el-form-item label="管理员状态">
          <el-switch
            v-model="isSuper"
            :active-value="1"
            :inactive-value="0"
            active-text="Super User"
            inactive-text="Normal"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="superDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitSuperChange" :loading="savingSuper">确 定</el-button>
      </span>
    </el-dialog>

    <!-- VIP Dialog -->
    <el-dialog
      title="开通VIP会员"
      :visible.sync="activateVipVisible"
      width="30%"
      :close-on-click-modal="false"
    >
      <el-form label-width="80px" size="small" class="edit-form" v-if="activateForm.user_id">
        <el-form-item label="会员类型" required>
          <el-select
            v-model="activateForm.vip_type_id"
            placeholder="请选择会员类型"
            filterable
            style="width: 100%"
            @change="onVipTypeChange">
            <el-option
              v-for="type in vipTypes"
              :key="type.id"
              :label="type.name_zh"
              :value="type.id">
              <span>{{ type.name_zh }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px" v-if="type.amount !== null && type.amount !== undefined">
                默认: ¥{{ Number(type.amount).toFixed(2) }}
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="会费金额">
          <el-input-number
            v-model="activateForm.amount"
            :min="0"
            :step="0.1"
            :precision="2"
            :controls="false"
            placeholder="不填则使用会员类型默认金额"
            style="width: 100%"
          />
          <div class="field-tip" v-if="selectedVipType && selectedVipType.amount">
            默认金额：¥{{ Number(selectedVipType.amount).toFixed(2) }}
          </div>
        </el-form-item>
        <el-form-item label="开始日期">
          <el-date-picker
            v-model="activateForm.start_date"
            type="date"
            placeholder="不填则默认为今天"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="结束日期">
          <el-date-picker
            v-model="activateForm.end_date"
            type="date"
            placeholder="不填则为永久会员"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="activateForm.notes"
            type="textarea"
            :rows="3"
            placeholder="备注"
            autocomplete="off"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="activateVipVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitActivateVip" :loading="activating">确 定</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import * as UAParser from 'ua-parser-js';
import { formatDateTime } from '@/utils/timeUtil'

export default {
  name: 'MiniUserDetail',
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      userDetail: {},
      memberships: [],
      vipTypes: [],
      loading: false,
      membershipLoading: false,
      savingUser: false,
      editingBasic: false,
      isSuper: 0,
      basicForm: {
        nickname: '',
        phone: '',
        gendervalue: '',
        is_vip: 0
      },
      activateVipVisible: false,
      activating: false,
      superDialogVisible: false,
      savingSuper: false,
      activateForm: {
        user_id: null,
        vip_type_id: null,
        amount: null,
        start_date: '',
        end_date: '',
        notes: ''
      }
    }
  },
  computed: {
    selectedVipType () {
      if (!this.activateForm.vip_type_id) return null
      return this.vipTypes.find(t => t.id === this.activateForm.vip_type_id)
    }
  },
  created () {
    this.getVipTypes()
    this.fetchDetail()
  },
  methods: {
    goBack () {
      this.$router.push('/admin/mini-users')
    },
    formatDate (value) {
      if (!value) return '--'
      return formatDateTime(value)
    },
    formatRange (start, end) {
      if (!start) return '--'
      if (!end) return `${start} ~ 未定`
      return `${start} ~ ${end}`
    },
    async fetchDetail () {
      this.loading = true
      try {
        const res = await this.request.get(`/namzar/admin/miniUsers/${this.id}`)
        const data = res.data || res
        this.userDetail = data
        this.isSuper = data.is_super || 0
        this.basicForm = {
          nickname: data.nickname,
          phone: data.phone,
          gendervalue: data.gendervalue,
          is_vip: data.is_vip
        }
        this.fetchMemberships(data.user_id)
      } catch (res) {
        this.$message.error(res?.message || '获取用户详情失败')
      } finally {
        this.loading = false
      }
    },
    async fetchMemberships (userId) {
      if (!userId) return
      this.membershipLoading = true
      try {
        const res = await this.request.get(`/namzar/admin/memberships/byUser/${userId}`)
        this.memberships = res.data || res || []
      } catch (res) {
        this.$message.error(res?.message || '获取会员记录失败')
      } finally {
        this.membershipLoading = false
      }
    },
    startBasicEdit () {
      this.editingBasic = true
      this.basicForm = {
        nickname: this.userDetail.nickname,
        phone: this.userDetail.phone,
        gendervalue: this.userDetail.gendervalue,
        is_vip: this.userDetail.is_vip
      }
    },
    cancelBasicEdit () {
      this.editingBasic = false
    },
    async saveBasicEdit () {
      this.savingUser = true
      try {
        await this.request.post('/namzar/admin/miniUsers/update', {
          id: this.userDetail.id || this.id,
          nickname: this.basicForm.nickname,
          phone: this.basicForm.phone,
          gendervalue: this.basicForm.gendervalue,
          is_vip: this.basicForm.is_vip
        })
        this.$message.success('更新成功')
        this.editingBasic = false
        this.fetchDetail()
      } catch (res) {
        this.$message.error(res?.message || '更新失败')
      } finally {
        this.savingUser = false
      }
    },
    goMembershipDetail (record) {
      this.$router.push({ path: `/admin/memberships/${record.id}`, query: { user_id: record.user_id } })
    },
    getVipTypes () {
      this.request
        .get('/namzar/admin/vipType')
        .then(res => {
          const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []
          this.vipTypes = data.map(item => ({
            id: item.id,
            name_zh: item.name_zh,
            name_ug: item.name_ug,
            amount: item.amount
          }))
        })
        .catch(res => {
          this.$message.error(res?.message || '获取会员类型失败')
        })
    },
    openActivateVip () {
      this.activateForm = {
        user_id: this.userDetail.user_id || this.id,
        vip_type_id: null,
        amount: null,
        start_date: '',
        end_date: '',
        notes: ''
      }
      this.activateVipVisible = true
    },
    onVipTypeChange (vipTypeId) {
      if (vipTypeId) {
        const selectedType = this.vipTypes.find(t => t.id === vipTypeId)
        if (!selectedType) return
        if (selectedType.amount !== null && selectedType.amount !== undefined) {
          this.activateForm.amount = selectedType.amount
        }
      }
    },
    async submitActivateVip () {
      if (!this.activateForm.user_id) {
        this.$message.warning('缺少用户ID')
        return
      }
      if (!this.activateForm.vip_type_id) {
        this.$message.warning('请选择会员类型')
        return
      }

      const payload = {
        user_id: this.activateForm.user_id,
        vip_type_id: this.activateForm.vip_type_id
      }
      
      // 可选字段
      if (this.activateForm.amount !== null && this.activateForm.amount !== undefined && this.activateForm.amount > 0) {
        payload.amount = this.activateForm.amount
      }
      if (this.activateForm.start_date) {
        payload.start_date = this.activateForm.start_date
      }
      if (this.activateForm.end_date) {
        payload.end_date = this.activateForm.end_date
      }
      if (this.activateForm.notes) {
        payload.notes = this.activateForm.notes
      }

      this.activating = true
      try {
        const res = await this.request.post('/namzar/admin/memberships/activate', payload)
        if (res && (res.code === 200 || res.message)) {
          this.$message.success(res.message || '开通VIP成功')
          this.activateVipVisible = false
          this.fetchDetail() // 刷新详情
          this.fetchMemberships(this.userDetail.user_id) // 刷新会员记录
        } else {
          this.$message.error(res.message || '开通VIP失败')
        }
      } catch (res) {
        this.$message.error(res?.message || '开通VIP失败')
      } finally {
        this.activating = false
      }
    },
    openSuperDialog () {
      this.isSuper = this.userDetail.is_super || 0
      this.superDialogVisible = true
    },
    async submitSuperChange () {
      this.savingSuper = true
      try {
        await this.request.post('/namzar/admin/miniUsers/update', {
          id: this.userDetail.id || this.id,
          is_super: this.isSuper
        })
        this.$message.success('更新成功')
        this.superDialogVisible = false
        this.userDetail.is_super = this.isSuper
        this.fetchDetail()
      } catch (res) {
        this.$message.error(res?.message || '更新失败')
        // 恢复原值
        this.isSuper = this.userDetail.is_super || 0
      } finally {
        this.savingSuper = false
      }
    },
    parseUserAgent(ua) {
      if (!ua) return '-';
      const parser = new UAParser.UAParser(ua);
      const device = parser.getDevice();
      const os = parser.getOS();
      const browser = parser.getBrowser();

      // 只展示品牌/型号/系统/浏览器
      let parts = [];
      if (device.vendor) parts.push(device.vendor);
      if (os.name) parts.push(os.name + (os.version ? ' ' + os.version : ''));
      return parts.length ? parts.join(' / ') : '-';
    }
  }
}
</script>

<style scoped>
.miniuser-detail {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100%;
}

.action-card {
  margin-bottom: 20px;
  background: #fff;
  border-radius: 12px;
}

.action-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.meta {
  display: flex;
  gap: 16px;
  color: #909399;
  font-size: 13px;
}

.right-actions {
  display: flex;
  gap: 10px;
}

.content-row {
  margin-top: 10px;
}

.info-card {
  margin-bottom: 20px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #ebeef5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.card-header {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 16px 24px;
  padding: 6px;
}

.info-item {
  background: #f8f9fb;
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid #ebeef5;
}

.info-item .label {
  margin: 0 0 8px;
  font-size: 13px;
  color: #909399;
}

.info-item .value {
  margin: 4px 0 0;
  font-size: 15px;
}

.membership-timeline {
  max-height: 500px;
  overflow-y: auto;
  padding: 10px;
}

.membership-timeline::-webkit-scrollbar {
  width: 6px;
}

.membership-timeline::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.membership-timeline::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.membership-timeline::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}

.timeline-item {
  background: #fafbfc;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 8px;
  border: 1px solid #ebeef5;
  transition: all 0.3s ease;
  margin-top: 12px;
}

.timeline-item:hover {
  cursor: pointer;
  background: #f6faff;
  border-color: #c8e0f6;
  box-shadow: 0 2px 8px rgba(46, 121, 201, 0.08);
}

.timeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.timeline-header .title {
  font-size: 14px;
  flex: 1;
}

.timeline-content {
  margin: 10px 0;
}

.timeline-content .info-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  font-size: 13px;
}

.timeline-content .info-row:last-child {
  margin-bottom: 0;
}

.timeline-content .info-label {
  color: #909399;
  min-width: 50px;
  flex-shrink: 0;
}

.timeline-content .info-value {
  color: #606266;
  flex: 1;
  word-break: break-word;
}

.timeline-content .info-value.amount {
  color: #f56c6c;
  font-weight: 500;
  font-size: 14px;
}

.empty-tip {
  text-align: center;
  color: #909399;
  padding: 20px 0;
}

.field-tip {
  font-size: 12px;
  color: #909399;
}
</style>

